/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import { derived, get, writable } from 'svelte/store';
import { calendarsViewDataModel, safeRefreshCalendars } from './provider.calendars.store';
// Removed calendarViewStore and calendarNavViewStore - using centralized range config instead

import type { Calendar, CalendarEvent, Transcript, TranscriptFull } from '@repo/common';
import { isAxiosError } from '@repo/toolslib/src/utils/catching.util';
import { dateComparisonStr } from '@repo/toolslib/src/utils/date.util';
import { fetchCalendarEvents } from '../data/calendar.events.data';
import { createStoreHandle, type DataModel } from '../utils/store.util';
import { enabledCalendars } from './selected.calendars.store';
import { calendarViewerStore } from './viewer.store';

const FILENAME = '.providers.calendar.events.dat';

const dummyCalendarsEvents: Calendar = {
  providerCalendarIds: [],
  name: '',
  type: '',
  eventsByDate: {}
};

const UPDATE_FREQUENCY = 10 * 60 * 1000; // 10 minutes in milliseconds

type DateRange = [Date, Date];

// Track if calendar events fetch is already in progress
let fetchInProgress = false;

import { isTranscriptFull } from '@repo/toolslib/src/utils/transcript.util';
import { getUnifiedRange } from './calendar-range.config';

const getStartEnd = (): DateRange => {
  // Use centralized range configuration that satisfies all downstream needs
  const unifiedRange = getUnifiedRange();

  console.log('[calendar.store] Using unified calendar range:', {
    description: 'Satisfies all downstream use cases (main view, nav, overview, agenda)',
    range: {
      start: unifiedRange.startISO,
      end: unifiedRange.endISO
    },
    debugInfo: unifiedRange
  });

  return [unifiedRange.start, unifiedRange.end];
};

// Create the calendar store handle
const {
  store: calendarDataModel,
  itemStore: calendarEvents,
  loadStoredItem: loadStoredCalendarsEvents,
  updateItem: _refreshCalendarsEvents,
  updateOutdatedItem: _updateOutdatedCalendarEvents
} = createStoreHandle<Calendar>(
  FILENAME,
  dummyCalendarsEvents,
  {
    updateTimeout: 5 * 60 * 1000, // 5 minutes
    retryTimeout: 5 * 1000 // 5 seconds
  },
  async (): Promise<Omit<DataModel<Calendar>, 'status' | 'lastUpdated'> | undefined> => {
    // Prevent multiple simultaneous fetch calls
    if (fetchInProgress) {
      console.log('[calendar.store] Calendar events fetch already in progress, skipping...');
      return;
    }

    try {
      fetchInProgress = true;
      let $enabledCalendars = get(enabledCalendars);
      let startEnd: DateRange;
      try {
        startEnd = getStartEnd();
      } catch (err) {
        console.warn('Could not determine calendar start/end', err);
        console.warn('CalendarViewer: ', get(calendarViewerStore));
        return;
      }
      const eventsByDate: Calendar['eventsByDate'] = {};
      for (
        const date = new Date(startEnd[0]);
        date <= startEnd[1];
        date.setDate(date.getDate() + 1)
      ) {
        const iterationDateStr = dateComparisonStr(date);
        eventsByDate[iterationDateStr] = {};
      }

      let $calendarsViewDataModel = get(calendarsViewDataModel);
      if ($calendarsViewDataModel.status !== 'loaded') {
        console.log('[fetchCalendarEvents] trying to refresh calendar providers');
        await safeRefreshCalendars();
        $calendarsViewDataModel = get(calendarsViewDataModel);
        await new Promise((resolve) => {
          let unsubscribe: () => void = () => {};
          const doUnsubscribe = () => {
            setTimeout(() => {
              unsubscribe?.();
            }, 100);
          };
          unsubscribe = calendarsViewDataModel.subscribe((data) => {
            if (data.status === 'loaded') {
              doUnsubscribe();
              resolve(true);
            }
          });
        });
      }
      $calendarsViewDataModel = get(calendarsViewDataModel);
      if ($calendarsViewDataModel.status !== 'loaded') {
        console.log(
          '[fetchCalendarEvents] calendarsViewDataModel is status: ',
          $calendarsViewDataModel.status,
          '; cannot get events'
        );
        throw new Error('Calendars not loaded');
      }
      $enabledCalendars = get(enabledCalendars);
      if ($enabledCalendars.length === 0) {
        console.log('[fetchCalendarEvents] no enabled calendars; cannot get events');
        throw new Error('Calendars not available');
      }
      console.log(
        '[fetchCalendarEvents] fetching calendar events for: ',
        startEnd[0],
        startEnd[1],
        $enabledCalendars
      );

      // Fetch function responsible for fetching the calendar and scheduling an update.

      const events = await fetchCalendarEvents($enabledCalendars, startEnd[0], startEnd[1]);

      for (const event of events || []) {
        if (!event.start) continue;
        if (!event.uid) continue;
        const eventStart = dateComparisonStr(new Date(event.start));
        if (!eventsByDate[eventStart]) {
          console.log('[fetchCalendarEvents] eventStart out of range: ', eventStart);
        }
        if (!eventsByDate[eventStart]) eventsByDate[eventStart] = {};
        // Use a record by uid to ensure no duplicates
        eventsByDate[eventStart][event.uid] = event;
      }

      const $calendarDataModel = get(calendarDataModel);

      return {
        needsPatch: false,
        nextUpdate: new Date(Date.now() + UPDATE_FREQUENCY).getTime(),
        item: {
          ...$calendarDataModel.item,
          eventsByDate: { ...$calendarDataModel.item.eventsByDate, ...eventsByDate }
        }
      };
    } catch (error) {
      if (isAxiosError(error)) {
        console.debug('[calendar.store] Error fetching calendar events:', error.message);
      } else {
        console.debug('[calendar.store] Error fetching calendar events:', error);
      }
      return;
    } finally {
      fetchInProgress = false;
    }
  }
);

export const calendarStatus = derived(
  calendarDataModel,
  ($calendarDataModel) => $calendarDataModel.status
);

let refreshCalendarsEventsPromise: Promise<void> | undefined = undefined;

export const refreshCalendarsEvents = async (): Promise<void> => {
  if (refreshCalendarsEventsPromise) {
    console.log(
      '[calendar.store] Calendar events refresh already in progress, returning existing promise...'
    );
    return refreshCalendarsEventsPromise;
  }

  try {
    refreshCalendarsEventsPromise = _refreshCalendarsEvents().then(() => {
      refreshCalendarsEventsPromise = undefined;
    });
  } catch (err) {
    console.log('[calendar.store] Error refreshing calendar events:', err);
  }
  return refreshCalendarsEventsPromise;
};

const lastUpdatedCalendarEventDays: Record<string, Date> = {};

/**
 * Updates calendar events that are outdated.
 * @returns true if any events were updated, false otherwise.
 */
const updateOutdatedCalendarEvents = async (): Promise<boolean> => {
  let startEnd: DateRange;
  try {
    startEnd = getStartEnd();
  } catch (err) {
    console.debug('Could not determine calendar start/end', err);
    console.debug('CalendarViewer: ', get(calendarViewerStore));
    return false;
  }
  const start = startEnd[0];
  const end = startEnd[1];
  const currentDate = new Date();
  const threshold = new Date(currentDate.getTime() - UPDATE_FREQUENCY);

  // Iterate through each day in the range
  const currentDay = new Date(start);
  while (currentDay <= end) {
    const dateStr = dateComparisonStr(currentDay);
    const lastUpdated = lastUpdatedCalendarEventDays[dateStr];

    // If the date hasn't been updated or is older than threshold
    if (!lastUpdated || lastUpdated < threshold) {
      // Refresh calendar events
      const task = refreshCalendarsEvents();

      // Update all dates in the range to current time to avoid concurrent fetches
      const updateDay = new Date(start);
      while (updateDay <= end) {
        const updateDateStr = dateComparisonStr(updateDay);
        lastUpdatedCalendarEventDays[updateDateStr] = currentDate;
        updateDay.setDate(updateDay.getDate() + 1);
      }
      await task;
      return true;
    }

    currentDay.setDate(currentDay.getDate() + 1);
  }
  return false;
};

export {
  calendarDataModel,
  calendarEvents,
  loadStoredCalendarsEvents,
  updateOutdatedCalendarEvents
};

export const getCalendarEventByTranscript = (transcript: Transcript): CalendarEvent | undefined => {
  if (!isTranscriptFull(transcript)) {
    return undefined;
  }
  transcript = transcript as TranscriptFull;
  if (!transcript?.meeting?.date) return undefined;
  if (!transcript?.eventId) return undefined;
  const dateStr = dateComparisonStr(new Date(transcript.meeting.date));
  const $calendarEvents = get(calendarEvents);
  const eventsByDate = $calendarEvents.eventsByDate[dateStr];
  if (!eventsByDate) return undefined;
  const event = eventsByDate[transcript.eventId];
  return event;
};

export const getCalendarEventById = (eventId: string, start: Date): CalendarEvent | undefined => {
  const $calendarEvents = get(calendarEvents);
  const dateStr = dateComparisonStr(start);
  const eventsByDate = $calendarEvents.eventsByDate[dateStr];
  if (!eventsByDate) return undefined;
  const event = eventsByDate[eventId];
  return event;
};

export const lastRefreshDate = writable('');
